import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalFooter } from "@/components/LegalFooter";
import {
  CAST_MS_POWER_TOP,
  CAST_MS_POWER_LOW,
  CAST_MS_ULTIMATE,
} from "@/lib/game/combat-core/cast";
import { INTRINSIC_BASIC_COOLDOWN_MS } from "@/lib/game/battle-v2/snapshot";

/**
 * Sekundenangaben zum Kampfablauf kommen aus den echten Konstanten
 * (combat-core/cast.ts bzw. dem Basis-Angriff im Snapshot-Bau) — hier steht
 * keine Zahl, die nicht auch im Kampf gilt.
 */
const CAST_MIN_S = CAST_MS_POWER_TOP / 1000;
const CAST_MAX_S = CAST_MS_POWER_LOW / 1000;
const CAST_ULT_S = CAST_MS_ULTIMATE / 1000;
const BASIC_CD_S = (INTRINSIC_BASIC_COOLDOWN_MS / 1000).toFixed(1).replace(".", ",");

const STEPS = [
  {
    name: "Konto erstellen und erstes Xenomon erwecken",
    text: "Registriere dich kostenlos mit E-Mail oder Google. Im Onboarding erschaffst du dein erstes einzigartiges Xenomon – Aussehen, Lore und Basis-Werte werden per KI generiert.",
  },
  {
    name: "Kampfsystem verstehen",
    text: "7 Move-Slots pro Xenomon: 6 für normale Attacken plus Slot 7 für die Ultimate. Jede Attacke holt erst aus, trifft am Ende des Ausholens und geht danach in die Abklingzeit. Wuchtige Attacken holen dabei kürzer aus als leichte. Auf einer neuen Stage tippst du deine Attacken selbst an; auf geräumten Stages kämpft dein Xenomon auf Wunsch allein.",
  },
  {
    name: "Aufsteigen und entfalten",
    text: "Jeder Sieg bringt XP. Mit jedem Aufstieg wachsen die Werte, und zu bestimmten Stufen lernt dein Xenomon eine neue Attacke. Auf Level 15, 30 und 50 entfaltet es sich in eine stärkere Form – dabei wählst du den Pfad: Berserker, Bollwerk oder Schnelle.",
  },
  {
    name: "Züchten und Team ausbauen",
    text: "Bringe zwei kompatible Eltern in die Brutkammer, um ein Ei zu erhalten – oder besiege einen Boss, der eines fallen lässt. Baue dir so ein Team mit unterschiedlichen Typen für jede Konter-Situation auf.",
  },
];

export const Route = createFileRoute("/how-to-play")({
  head: () => ({
    meta: [
      { title: "Xenomons spielen — Anleitung für Einsteiger" },
      { name: "description", content: "Die komplette Xenomons-Anleitung: Kampfsystem, 5 Typen, Entfaltung, Züchten und Expeditionen. Schritt für Schritt erklärt." },
      { property: "og:title", content: "Xenomons spielen — Anleitung für Einsteiger" },
      { property: "og:description", content: "Von deinem ersten Xenomon bis zum vollen Team: Kampfsystem, Typen-Konter, Entfaltung und Züchten Schritt für Schritt." },
      { property: "og:url", content: "https://xenomons.online/how-to-play" },
    ],
    links: [{ rel: "canonical", href: "https://xenomons.online/how-to-play" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "Xenomons spielen – Anleitung für Einsteiger",
          description: "Schritt-für-Schritt-Anleitung für den Einstieg in Xenomons, den KI-Auto-Battler im Browser.",
          step: STEPS.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.name,
            text: s.text,
          })),
        }),
      },
    ],
  }),
  component: HowToPlayPage,
});

function HowToPlayPage() {
  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-30" aria-hidden />
      <main className="relative z-10 mx-auto max-w-3xl px-6 py-16">
        <p className="mb-3 text-xs uppercase tracking-widest text-primary">Anleitung</p>
        <h1 className="font-display text-4xl font-black leading-tight md:text-5xl">
          Xenomons spielen — Die komplette Anleitung für Einsteiger
        </h1>
        <p className="mt-4 text-muted-foreground">
          Xenomons ist ein Auto-Battler mit KI-generierten Cyber-Kreaturen, der direkt im Browser läuft.
          Diese Anleitung führt dich vom ersten Xenomon bis zum vollen Team. Fragen offen? Schau in die{" "}
          <Link to="/faq" className="text-primary hover:underline">FAQ</Link>.
        </p>

        <section className="mt-10">
          <h2 className="font-display text-2xl font-bold">Dein erstes Xenomon</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Nach der Registrierung führt dich das Onboarding durch die Erschaffung deiner ersten Kreatur.
            Aussehen, Name und Basiswerte werden per KI generiert – jedes Xenomon ist ein Unikat und bleibt
            dauerhaft mit deinem Konto verknüpft.
          </p>
        </section>

        <section className="mt-8">
          <h2 className="font-display text-2xl font-bold">Kampfsystem verstehen</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Jedes Xenomon hat 7 Move-Slots: 6 für normale Attacken plus einen 7. Slot für die
            Ultimate. Du verwaltest deine gelernten Moves
            (lernen, vergessen, Slots tauschen). Jede Attacke ist ab dem Startsignal sofort
            verfügbar und läuft dann immer denselben Zyklus: ausholen, treffen, abkühlen. Wie lange
            ausgeholt wird, hängt an der Wucht der Attacke — und zwar umgekehrt, als man erwartet:
            Leichte Dauerfeuer-Attacken brauchen die vollen {CAST_MAX_S} Sekunden, die wuchtigsten
            nur {CAST_MIN_S} Sekunden. Das lange Ausholen ist der Preis für einen kurzen Cooldown,
            nicht für hohen Schaden. Einzige Ausnahme ist die Ultimate: sie holt fest{" "}
            {CAST_ULT_S} Sekunden aus, egal wie stark sie ist. Jede Attacke holt dabei für sich
            aus: Mehrere können gleichzeitig ausholen, jede mit ihrem eigenen Zyklus — nur dieselbe
            Attacke nicht doppelt. Ein begonnener Schlag landet, und abbrechen kannst du ihn
            nicht. Der eingebaute Basis-Angriff
            braucht kein Ausholen und trifft alle {BASIC_CD_S} Sekunden, wenn gerade keine andere
            Attacke bereit ist. Gegner mit eigenen Attacken holen genauso aus — du siehst auf der
            Gegnerkarte, was als Nächstes einschlägt.
          </p>
        </section>

        <section className="mt-8">
          <h2 className="font-display text-2xl font-bold">Die 5 Typen und ihre Konter</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Die fünf gleichwertigen Typen bilden einen Ring: Jeder kontert genau einen anderen und wird von einem anderen gekontert. Einen Neutral-Typ gibt es nicht.
          </p>
          <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground">
            <li><strong className="text-foreground">Neon</strong> ist stark gegen Void.</li>
            <li><strong className="text-foreground">Void</strong> ist stark gegen Plasma.</li>
            <li><strong className="text-foreground">Plasma</strong> ist stark gegen Data.</li>
            <li><strong className="text-foreground">Data</strong> ist stark gegen Glitch.</li>
            <li><strong className="text-foreground">Glitch</strong> ist stark gegen Neon.</li>
          </ul>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Im Vorteil triffst du spürbar härter, im Nachteil deutlich schwächer. Wichtig dabei:
            Es zählt der Typ der Attacke, mit der du zuschlägst, nicht der deines Xenomons — mit
            der richtigen Attacke kannst du also kontern, obwohl dein Xenomon selbst im Nachteil
            steht. Neutrale Attacken bleiben immer gleich stark. Wer den Ring kennt, gewinnt auch
            gegen stärkere Gegner.
          </p>
        </section>

        <section className="mt-8">
          <h2 className="font-display text-2xl font-bold">Zonen und Stages</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Es gibt 20 Zonen mit je 10 Stages – insgesamt 200 Stages. Jede Stage bringt stärkere
            Gegner und besseren Loot; auf jeder Stage 10 wartet ein Zonen-Boss. Bosse verlangen
            Typen-Wissen und gute Move-Auswahl. Verlierst du auf einer Frontier-Stage, geht es am
            letzten Checkpoint weiter – Fortschritt verlierst du nicht.
          </p>
        </section>

        <section className="mt-8">
          <h2 className="font-display text-2xl font-bold">Selber spielen oder zusehen</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Eine Stage, die du zum ersten Mal betrittst, spielst du von Hand: Dort feuert automatisch
            nur der eingebaute Basis-Angriff, deine gelernten Attacken tippst du selbst an, sobald sie
            bereit sind. Auf einer Stage, die du bereits geräumt hast, kannst du den Auto-Kampf
            einschalten und zusehen – das ist Training und bringt die halbe Belohnung. Offline kämpft
            dein Xenomon nicht; nur Expeditionen und Brutzeiten laufen weiter, während du weg bist.
          </p>
        </section>

        <section className="mt-8">
          <h2 className="font-display text-2xl font-bold">Aufstieg und Entfaltung</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Jeder besiegte Gegner bringt XP. Mit jedem Aufstieg wachsen die Werte. Neue Attacken
            kommen dagegen zu bestimmten Stufen: am Anfang dicht hintereinander, später in größeren
            Abständen – und wann genau, ist bei jedem Xenomon etwas anders. Weitere Slots öffnen sich
            auf Level 7, 12 und 15; Slot 7 für die Ultimate ab Level 30. Ist kein Slot mehr frei,
            fragt dich das Spiel, ob die neue Attacke eine alte ersetzen soll. Auf
            Level 15, 30 und 50 entfaltet sich dein Xenomon in eine stärkere Form: Du wählst den Pfad
            (Berserker, Bollwerk oder Schnelle), und das Aussehen wird neu erschaffen. Die dritte
            Stufe verlangt zusätzlich einen Apex-Kristall – den lassen sehr selten Bosse ab Zone 5
            fallen.
          </p>
        </section>

        <section className="mt-8">
          <h2 className="font-display text-2xl font-bold">Züchten</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Zwei kompatible Eltern legen in der Brutkammer ein Ei. So baust du dir gezielt Xenomons
            mit gewünschten Typ-Kombinationen auf. Eier fallen außerdem im Kampf: Der Boss von Zone 2
            lässt garantiert eines fallen, spätere Bosse und Elite-Gegner mit etwas Glück – aber nur
            beim ersten Sieg auf einer Stage. Ein so gefundenes Ei schlüpft im Typ des Gegners, der es
            fallen ließ. Ausbrüten musst du jedes Ei in der Brutkammer, es braucht seine Brutzeit.
          </p>
        </section>

        <section className="mt-8">
          <h2 className="font-display text-2xl font-bold">Expeditionen</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Schicke ein Xenomon auf einen automatischen Beutezug. Es gibt drei feste Dauern: 30
            Minuten, 2 Stunden und 8 Stunden. Als Belohnung gibt es Gold, XP und mit etwas Glück
            seltene Gegenstände (Loot). Wählst du eine Zone, deren Leittyp zum Typ deines Xenomons
            passt, gibt es einen Gold-Bonus. Zwei Risikostufen (sicher/riskant) stehen zur Wahl.
            Eier gibt es aus Expeditionen nicht.
          </p>
        </section>

        <section className="mt-8">
          <h2 className="font-display text-2xl font-bold">Tipps für Einsteiger</h2>
          <ul className="mt-3 list-disc space-y-2 pl-6 text-sm leading-relaxed text-muted-foreground">
            <li>In den Kampf geht immer nur ein Xenomon. Vor einem Boss lohnt es sich, im Stall auf einen passenden Typ zu wechseln.</li>
            <li>Auf einer neuen Stage selbst antippen: Der Basis-Angriff feuert nur, wenn gerade keine deiner Attacken bereit ist.</li>
            <li>Verkaufe niedrig-rare Drops per Bulk-Verkauf – Inventar-Slots sind wertvoller als Gold.</li>
            <li>Starte Expeditionen bevor du das Fenster schließt – sie laufen im Hintergrund weiter.</li>
          </ul>
        </section>


        <div className="mt-12 flex flex-wrap gap-3">
          <Link
            to="/auth"
            className="inline-block rounded-lg bg-gradient-to-r from-primary to-accent px-6 py-3 font-display font-bold text-primary-foreground shadow-glow hover:scale-105 transition-transform"
          >
            Jetzt kostenlos spielen
          </Link>
          <Link
            to="/faq"
            className="inline-block rounded-lg border border-primary/40 bg-primary/10 px-6 py-3 font-display font-bold text-primary hover:bg-primary/20"
          >
            Zur FAQ
          </Link>
        </div>
      </main>
      <LegalFooter />
    </div>
  );
}
